import { useEffect, useRef, useState } from "react";

export type MenuNavMode = "pointer" | "keyboard";

/**
 * Which input is driving the highlight, so a hovered row and a keyboard-focused
 * row can look different. cmdk gives the mouse and the arrow keys the same
 * state, and the DOM does not report modality.
 *
 * Pass `open` from a menu that stays mounted while closed, or its mode leaks
 * into the next opening.
 */
export function useMenuNavMode(open?: boolean) {
  const [navMode, setNavMode] = useState<MenuNavMode>("pointer");
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (open === false) {
      setNavMode("pointer");
      lastPoint.current = null;
    }
  }, [open]);

  return {
    navMode,
    navHandlers: {
      onPointerMove: (event: { clientX: number; clientY: number }) => {
        // Some browsers fire `pointermove` at the cursor's resting position
        // when the list opens, so an unmoved pointer must not count.
        const previous = lastPoint.current;
        lastPoint.current = { x: event.clientX, y: event.clientY };
        if (!previous) return;
        if (previous.x === event.clientX && previous.y === event.clientY)
          return;
        setNavMode("pointer");
      },
      onKeyDown: (event: { key: string }) => {
        // Any key: cmdk moves its highlight on every keystroke typed into the
        // search box, not only on the arrows.
        if (event.key !== "Shift" && event.key !== "Control") {
          setNavMode("keyboard");
        }
      },
    },
  };
}
